import { useEffect, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import type { SortingState } from '@tanstack/react-table';

import { getClientInsights } from 'api/generated/client-insights/client-insights';
import type { InsightDto } from 'api/generated/schemas/insightDto';

import { useCurrentUser } from 'state/current-user/current-user.thunk';

export type InsightSummary = InsightDto;

interface UseInsightsArgs {
  clientId: string | number | undefined;
}

interface UseInsightsResult {
  insights: InsightSummary[];
  initialLoading: boolean;
  isLoading: boolean;
  sortingParams: SortingState;
  setSortingParams: Dispatch<SetStateAction<SortingState>>;
}

export const useInsights = ({
  clientId,
}: UseInsightsArgs): UseInsightsResult => {
  const [insights, setInsights] = useState<InsightSummary[]>([]);
  const [initialLoading, setInitialLoading] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [sortingParams, setSortingParams] = useState<SortingState>([
    { id: 'createdDate', desc: true },
  ]);

  const currentUser = useCurrentUser();
  const userId = currentUser?.data?.oktaUserId;

  const loadInsights = async (): Promise<void> => {
    if (!userId || !clientId) return;

    setIsLoading(true);
    try {
      const list = await getClientInsights(userId, Number(clientId), {
        page: 0,
        size: 20,
        sort: sortingParams[0]
          ? [
              `${sortingParams[0].id},${sortingParams[0].desc ? 'desc' : 'asc'}`,
            ]
          : undefined,
      });

      setInsights(list ?? []);
    } catch (error) {
      console.error('Error loading insights:', error);
    } finally {
      setIsLoading(false);
      setInitialLoading(false);
    }
  };

  useEffect(() => {
    loadInsights();
  }, [userId, clientId, sortingParams]);

  return {
    insights,
    initialLoading,
    isLoading,
    sortingParams,
    setSortingParams,
  };
};
